// src/components/climate.js (s110) — plain-JS helpers for ClimateStrip.jsx and
// scripts/probe-climate-render.mjs (Node cannot import .jsx). Inputs: one entry of
// src/data/climateNormals.js (NOAA 1991–2020 normals, nearest station).
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const START = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
const md = (s) => { const m = /^(\d\d)-(\d\d)$/.exec(s || ''); return m ? { m: +m[1], d: +m[2] } : null; };
const fmt = ({ m, d }) => `${MONTHS[m - 1]} ${d}`;
// NOAA's 50%-probability dates: first fall freeze → last spring freeze. A span of three
// weeks or less (San Francisco Dec 25 → Dec 30) is a freeze in about half of winters.
export function freezeSeason(c) {
  // 'rare' = a 32 F night in fewer than half of years; with no freezing nights at all, None.
  if (c.lastFreeze === 'rare' || c.firstFreeze === 'rare') return c.frz.reduce((x, y) => x + y, 0) < 0.5 ? 'None' : 'Rare';
  const a = md(c.firstFreeze), b = md(c.lastFreeze);
  if (!a || !b) return '—';
  const len = ((START[b.m - 1] + b.d) - (START[a.m - 1] + a.d) + 365) % 365;
  return len <= 21 ? `Occasional, near ${fmt(a)}` : `${fmt(a)} – ${fmt(b)}`;
}
// Frost month: 3 or more nights below 32 F on average (NOAA's own count). A normal low
// of 33 F (Atlanta in January) still brings a dozen freezing nights, so the low alone misleads.
export const isFrost = (c, i) => c.frz[i] >= 3;
// Best months: frost-free AND at or below the median rain days of the frost-free months,
// so a short northern summer is not judged against dry frozen months.
export function bestMonths(c) {
  const free = c.frz.map((_, i) => !isFrost(c, i));
  const r = c.rain.filter((_, i) => free[i]).sort((x, y) => x - y);
  if (!r.length) return c.rain.map(() => false);
  const med = r.length % 2 ? r[(r.length - 1) / 2] : (r[r.length / 2 - 1] + r[r.length / 2]) / 2;
  return c.rain.map((d, i) => free[i] && d <= med);
}
export function monthRanges(flags) {
  if (flags.every(Boolean)) return 'All year';
  if (!flags.some(Boolean)) return '—';
  const start = flags.findIndex((f, i) => f && !flags[(i + 11) % 12]); // a run start (circular)
  const runs = []; let run = null;
  for (let k = 0; k < 12; k++) { const i = (start + k) % 12; if (flags[i]) { if (run) run[1] = i; else run = [i, i]; } else if (run) { runs.push(run); run = null; } }
  if (run) runs.push(run);
  return runs.map(([a, b]) => (a === b ? MONTHS[a] : `${MONTHS[a]}–${MONTHS[b]}`)).join(', ');
}
export const rainDaysYear = (c) => Math.round(c.rain.reduce((a, b) => a + b, 0));
export const normalsCsv = (st) => `https://www.ncei.noaa.gov/data/normals-monthly/1991-2020/access/${st}.csv`;
const LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
// "April and August to October", "March to June, September and November", "all year".
export function monthRangesLong(flags) {
  if (flags.every(Boolean)) return 'all year';
  const short = monthRanges(flags); if (short === '—') return null;
  const parts = short.split(', ').map((p) => { const [x, y] = p.split('–'); if (!y) return LONG[MONTHS.indexOf(x)]; const adj = (MONTHS.indexOf(y) - MONTHS.indexOf(x) + 12) % 12 === 1; return `${LONG[MONTHS.indexOf(x)]} ${adj ? 'and' : 'to'} ${LONG[MONTHS.indexOf(y)]}`; });
  return parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}
// Plain-text freeze sentence for answers (FAQPage JSON-LD); mirrors freezeSeason().
export function freezeSentence(c) {
  const s = freezeSeason(c);
  if (s === 'None') return 'Frost nights essentially never come here.';
  if (s === 'Rare') return 'A frost night comes in fewer than half of years.';
  if (s === '—') return null;
  const full = (t) => t.replace(/\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b/g, (m) => LONG[MONTHS.indexOf(m)]);
  if (s.startsWith('Occasional')) return `A frost night comes in about half of winters, near ${full(s.replace('Occasional, near ', ''))}.`;
  const [a, b] = s.split(' – ');
  return `Frost nights usually run from ${full(a)} to ${full(b)}, so plan exterior washes outside that stretch.`;
}
// s134 Phase 5c: the NOAA station as a reader-facing phrase for FAQ answers —
// "the Huntsville International Airport station", "a station near Northport".
// NOAA writes names in capitals with its own abbreviations; expand the common
// ones and title-case the rest. A trailing "2 S"/"1 NE" is NOAA's distance and
// bearing from a named place, so it reads as "near <place>".
const ST_ABBR = { AP: 'Airport', ARPT: 'Airport', INTL: 'International', MUNI: 'Municipal', FLD: 'Field', RGNL: 'Regional', DWTN: 'Downtown', CO: 'County', FT: 'Fort', MT: 'Mount', MEM: 'Memorial', EXP: 'Experiment', STN: 'Station', GTR: 'Greater', EXEC: 'Executive', UNIV: 'University', CNTRL: 'Central', TWR: 'Tower', SCI: 'Science', CTR: 'Center', PK: 'Park', RD: 'Road', PT: 'Point', ST: 'St.', U: 'University', AG: 'Agricultural', JR: 'Jr.' };
const ST_KEEP = new Set(['ASU', 'BYU', 'JFK', 'NAS', 'AFB', 'MCAS', 'ANG', 'WSFO', 'WSO', 'NE', 'NW', 'SE', 'SW']);
export function stationPhrase(name) {
  const raw = String(name || '').replace(/,\s*[A-Z]{2}\s+US$/, '').replace(/\s+-\s+/g, '-').trim();
  if (!raw) return null;
  const near = raw.match(/^(.*?)\s+\d+(?:\.\d+)?\s*[NSEW]{1,3}$/) || raw.match(/^(.*?)\s+\d+[NSEW]{1,3}$/);
  const words = (near ? near[1] : raw).split(/\s+/).map((w) => w.split('-').map((p) => {
    if (ST_ABBR[p]) return ST_ABBR[p];
    if (ST_KEEP.has(p) || /\d/.test(p)) return p;
    return p.charAt(0) + p.slice(1).toLowerCase();
  }).join('-'));
  const label = words.join(' ').replace(/\bOf\b/g, 'of');
  return near ? `a station near ${label}` : `the ${label} station`;
}

// s135 PHASE 5 — CLEANING CALENDAR (DECISIONS #51). Pure; the city page (ClimateStrip), the gate
// (scripts/verify-cleaning-calendar.mjs) and the probe all call this one function. Inputs are figures
// already captured: the NOAA normals above, the USA-NPN first-bloom date (airLoad pollen) and NOAA HMS
// medium/heavy smoke days by month (airLoad smoke.byMonth, 2023–2025 average). Nothing is estimated.
// A smoke month is RECURRING: SMOKE_MONTH_DAYS or more smoke days in at least SMOKE_MONTH_YEARS of the three
// years (smoke.monthYears), so a single fire season (June 2023's Canadian smoke) does not mark a month for good. Wash months are always best months
// (bestMonths above), picked by three rules, the reasoning for which lives once on /methodology#calendar:
//   bloom  — the first best month after the month of first bloom (the pollen film is down by then);
//   smoke  — the first best month after the last month of the longest smoke run;
//   frost  — the last best month before the first month of the longest frost run that is not a smoke month
//            (glass goes into winter clean); dropped when every such month is a smoke month.
export const SMOKE_MONTH_DAYS = 2;
export const SMOKE_MONTH_YEARS = 2;
const LONGM = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export function bloomMonth(pollen) {
  if (!pollen || pollen.s !== 'captured' || !pollen.date) return null;
  const i = LONGM.indexOf(String(pollen.date).split(' ')[0]);
  return i < 0 ? null : i;
}
// Longest run of flagged months, circular: { start, end, len } or null. All 12 flagged → null (no edge).
function longestRun(flags, weight) {
  if (!flags.some(Boolean) || flags.every(Boolean)) return null;
  const start = flags.findIndex((f, i) => f && !flags[(i + 11) % 12]);
  let best = null, run = null;
  for (let k = 0; k < 12; k++) {
    const i = (start + k) % 12;
    if (flags[i]) { if (run) { run.end = i; run.len++; run.w += weight(i); } else run = { start: i, end: i, len: 1, w: weight(i) }; }
    else if (run) { if (!best || run.w > best.w) best = run; run = null; }
  }
  if (run && (!best || run.w > best.w)) best = run;
  return best;
}
const nextBest = (best, from) => { for (let k = 1; k <= 12; k++) { const i = (from + k) % 12; if (best[i]) return i; } return null; };
const prevBest = (best, from) => { for (let k = 1; k <= 12; k++) { const i = (from - k + 12) % 12; if (best[i]) return i; } return null; };
export function cleaningCalendar(c, air) {
  if (!c) return null;
  const best = bestMonths(c);
  const bloom = bloomMonth(air?.pollen);
  const sm = air?.smoke?.s === 'captured' && Array.isArray(air.smoke.byMonth) ? air.smoke.byMonth : null;
  const my = sm && air.smoke.monthYears ? Object.values(air.smoke.monthYears) : null;
  const smokeFlags = my ? sm.map((_, i) => my.filter((y) => y[i] >= SMOKE_MONTH_DAYS).length >= SMOKE_MONTH_YEARS) : Array(12).fill(false);
  const frostFlags = c.frz.map((_, i) => isFrost(c, i));
  const smokeRun = longestRun(smokeFlags, (i) => sm[i]);
  const frostRun = longestRun(frostFlags, (i) => c.frz[i]);
  // The pre-frost wash has to last the winter, so it may not fall in a smoke month; the other two use any best month.
  const ok = best.map((b, i) => b && !smokeFlags[i]);
  const picks = [];
  if (best.some(Boolean)) {
    if (bloom !== null) { const m = nextBest(best, bloom); if (m !== null) picks.push({ why: 'bloom', m }); }
    if (smokeRun) { const m = nextBest(best, smokeRun.end); if (m !== null) picks.push({ why: 'smoke', m }); }
    if (frostRun) { const m = prevBest(ok, frostRun.start); if (m !== null) picks.push({ why: 'frost', m }); }
  }
  const wash = Array(12).fill(false); picks.forEach((p) => { wash[p.m] = true; });
  return { best, bloom, smoke: sm, smokeFlags, smokeRun, frostRun, picks, wash,
    washText: wash.some(Boolean) ? MONTHS.filter((_, i) => wash[i]).join(', ') : '—',
    smokeText: sm ? (smokeFlags.some(Boolean) ? monthRanges(smokeFlags) : 'None') : null };
}
